import {
  html,
  TemplateResult,
  css,
} from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { nothing } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import variables from '../../common/styles/variables';
import chevronRightIcon from '../../common/icons/chevron-right-icon';
import store from '../../store/store';
import {connect} from 'pwa-helpers';
import debounce from 'lodash/debounce';
import { FormBaseComponent } from "../base-component/forms/form-base-component";
import { booleanConverter } from "../../common/converters";
import { getUniqueId } from "../../common/utils/util";
import emit from "../../common/events";
import { getAutoCompleteSuggestionsAsync, getSuggestions, isSuggestionsFailure, isSuggestionsLoaded, isSuggestionsLoading, isSuggestionsReset, resetSuggestionsState } from "./store/address-suggestion";
import { StoreState } from "../../store/store.interface";
import { Suggestion } from "./interfaces/address.interface";
import { addAnalyticsCheckoutFlowMetadata } from "../../common/utils/cart-util";
import { getDoubleQuoteErrorMessage, getDoubleQuoteValidationPattern } from '../../common/utils/address-util';

export enum AddressType {
  BILLING = 'billing',
  SHIPPING = 'shipping',
}

/**
 * An input element to get address suggestions for US and international addresses.
 * 
 * @event mhecomm-address-changed - Emitted when address input text changes.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: {id, changedAddress}
 * @event mhecomm-address-selector-error - Emitted when there is an error.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: {id, error}
 */
@customElement('mhecomm-address-selector')
export class AddressSelector extends connect(store)(FormBaseComponent) {
  static styles = [
    variables,
    css`
      [part='us-autocomplete-pro-menu'] {
        padding: 0px;
        margin: 0px;
        list-style-type: none;
        background-color: var(--mh-white);
        border: 1px solid var(--mh-light-gray);
        border-top: none;
      }
      .focusedAddress {
        background-color: var(--mh-light-gray-2);
      }
      [part='us-autocomplete-pro-menu'] li {
        padding: 10px;
        cursor: pointer;
      }
      [part='us-autocomplete-pro-menu'] li:hover {
        background-color: var(--mh-light-gray-2);
      }
    `,
  ];

  /**
   * Country that address belongs to
   */
  @property()
  country = '';
  /**
   * Type of address. Ex. Billing or Shipping
   */
  @property()
  type = '';
  /**
   * Address selector input element text value
   */
  @property()
  addressInput = '';

  /**
   * Text to display when no suggestions are
   * found for address input text in address autocomplete type-ahead input element
   */
  @property()
  noSuggestionsFoundText = '';

  @property()
  suggestionsLoadingText = 'Loading suggestions';

  /**
   * placeholder text for address autocomplete type-ahead input element
   */
  @property()
  placeholder = 'Enter Address';

  @property({ converter: booleanConverter })
  disabled = false;

  /**
   * Text to describe mhecomm-address-selector
   */
  @property()
  ariaDescriptionText = 'When autocomplete address suggestions are available use up and down arrows to review and enter to select.';

  /**
   * Flag to toggle whether to filter Po Box addresses
   */
  @property({ converter: booleanConverter })
  shouldFilterPoBoxAddress = false;

  /**
   * Is Address Input required field
   */
  @property({converter: booleanConverter})
  required = false;

  /**
   * keep label inside selector to fucus on input
   */
  @property()
  label = '';

  /**
   * List of address suggestions
   */
  @state()
  private addressMenu: TemplateResult | TemplateResult[] | typeof nothing | object = nothing;

  /**
   * focus index of the address menu item focused using arrow keys
   */
  @state()
  private currentFocus = -1;

  @state()
  private isAddressMenuHidden = false;

  @state()
  private mouseOverMenu = false;

  _id = getUniqueId();

  @state()
  private isAriaExpanded = false;

  constructor() {
    super();
    this.addEventListener('input', debounce(this._inputUpdate, 500));
  }

  renderComp() {
    return html`
      <form novalidate @submit=${this.submit}>
        ${this.label ? html`<label for="input-${this.id}" class="form-label">${this.label}</label>` : nothing}
        <input class="form-control ${this.showValidationStying() ? 'is-invalid' : ''}"
          part="us-autocomplete-pro-address-input"
          id="input-${this.id}"
          type="text"
          placeholder="${this.placeholder}"
          .value=${this.addressInput}
          ?disabled=${this.disabled}
          ?required=${this.required}
          @keydown=${this._addressMenuKeyboardNav}
          @focusout=${this._focusOutMenu}
          @=${this._focusOutMenu}
          autocomplete="off" 
          aria-owns="${this.id}-us-autocomplete-pro-menu"
          aria-autocomplete="both"
          aria-describedby="addressSelectorDescription"
          aria-activedescendant=${ifDefined(this.currentFocus !== -1 && this.addressInput ? `active-option-${this.currentFocus}` : undefined)}
          aria-expanded=${this.isAriaExpanded}
          role="combobox"
          pattern="${getDoubleQuoteValidationPattern()}"
        />
        <ul id="${this.id}-us-autocomplete-pro-menu" 
          class="mb-1"
          @mouseover=${this._mouseOverMenu}
          @mouseout=${this._mouseOutMenu}
          part="us-autocomplete-pro-menu" role="listbox" ?hidden="${this.isAddressMenuHidden}">
          ${this.addressMenu}
        </ul>
        <span id="addressSelectorDescription" style="display: none;">${this.ariaDescriptionText}</span>
      </form>`;
      
  }

  protected validationMessage() {
    return {
      [`input-${this.id}`]: {
        required: 'Please fill in this field.',
        customError: this.errorMessage,
        pattern: getDoubleQuoteErrorMessage('Address'),
      },
    };
  }

  /**
   * When user focus out of component hide address suggestion menu
   */
  private _focusOutMenu(){
    if(!this.mouseOverMenu){
      this.hideAddressMenu();
    }
  }
  
   /**
   * When user mouse is over menu
   */
    private _mouseOverMenu(){
      this.mouseOverMenu = true;
    }

  /**
   * When user mouse out of menu
   */
  private _mouseOutMenu(){
    this.mouseOverMenu = false;
  }

  /**
   * Triggered when address is input in us-autocomplete-pro-address-input element
   */
  private _inputUpdate() {
    this.searchAddress(this.inputElement()!.value);
  }

  public searchAddress(addressSearchQuery: string) {
    this.addressInput = addressSearchQuery;
    if (!this.disabled && this.country === 'US' && this.addressInput!.length >= 3) {
      emit({
        element: this,
        name: 'mhecomm-address-changed',
        detail: addAnalyticsCheckoutFlowMetadata({id: this.id, changedAddress: this.addressInput}),
      });
      store.dispatch(getAutoCompleteSuggestionsAsync(this._id, this.addressInput));
    } else {
      this.hideAddressMenu();
    }
  }

  /**
   * Keyboard navigation of us-autocomplete-pro-menu. Triggered for keydown event on us-autocomplete-pro-address-input
   */
  private _addressMenuKeyboardNav(e: KeyboardEvent) {
    const addressMenuListItems = this.addressMenuListItems();
    if (addressMenuListItems.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          this.currentFocus++;
          this.focusListElement(addressMenuListItems);
          break;
        case 'ArrowUp':
          this.currentFocus--;
          this.focusListElement(addressMenuListItems);
          break;
        case 'Enter':
          addressMenuListItems[this.currentFocus].click();
          break;
        case 'Escape':
          this.hideAddressMenu();
          break;
      }
    }
  }

  stateChanged(state: StoreState) {
    if (!isSuggestionsReset(this._id, state)){
      if (isSuggestionsFailure(this._id, state)) {
        emit({
          element: this,
          name: 'mhecomm-address-selector-error',
          detail: {id: this.id, error: getSuggestions(this._id, state)},
        });
      }
      if (isSuggestionsLoading(this._id, state)) {
        this.loadingAddressMenu();
      }
      if (isSuggestionsLoaded(this._id, state)) {
        let suggestionsData = getSuggestions(this._id, state);
        if (suggestionsData) {
          if(this.shouldFilterPoBoxAddress){
            suggestionsData = this.filterPoBoxAddresses(suggestionsData);
          }
          // auto select address if only one suggestion is present
          if(suggestionsData.length === 1){
            store.dispatch(resetSuggestionsState(this._id));
            this._addressSelected(suggestionsData[0])
          }
          else{
            this.addressMenu = this.buildAddressMenu(suggestionsData);
            this.inputElement().focus();
            store.dispatch(resetSuggestionsState(this._id));
          }
        }
      }
    }
  }

  /**
   * Focus on us-autocomplete-pro-menu li element hovered by keyboard up or down keys.
   * @param addressMenuListItems 
   */
  private focusListElement(
    addressMenuListItems: HTMLCollectionOf<HTMLElement>
  ) {
    this.unFocusListElement(addressMenuListItems);
    if (this.currentFocus >= addressMenuListItems.length) this.currentFocus = 0;
    if (this.currentFocus < 0)
      this.currentFocus = addressMenuListItems.length - 1;
    addressMenuListItems[this.currentFocus].classList.add('focusedAddress');
  }

  /**
   * Un-Focus on us-autocomplete-pro-menu li element hovered by keyboard up or down keys.
   * @param addressMenuListItems 
   */
  private unFocusListElement(
    addressMenuListItems: HTMLCollectionOf<HTMLElement>
  ) {
    for (let i = 0; i < addressMenuListItems.length; i++) {
      addressMenuListItems[i].classList.remove('focusedAddress');
    }
  }

  /**
   * Intermediate state of us-autocomplete-pro-menu when suggestions are loading
   */
  private loadingAddressMenu() {
    this.addressMenu = html`<li>${this.suggestionsLoadingText}</li>`;
  }

  /**
   * Show us-autocomplete-pro-menu
   */
   private showAddressMenu() {
    this.isAriaExpanded = true;
    this.isAddressMenuHidden = false;
  }
  
  /**
   * Hide us-autocomplete-pro-menu
   */
  private hideAddressMenu() {
    this.isAriaExpanded = false;
    this.isAddressMenuHidden = true;
  }

  /**
   * On select address suggestion in us-autocomplete-pro-menu
   * @param suggestion 
   */
  private _addressSelected(suggestion: Suggestion) {
    this.currentFocus = -1;
    this.unFocusListElement(this.addressMenuListItems());
    this.addressInput = suggestion.street1 + 
      (suggestion.street2 ? ' ' + suggestion.street2 : '');
    if (suggestion.entries > 1) {
      const secondaryExpansion: string = suggestion.secondaryExpansion
        ? suggestion.secondaryExpansion
        : '';
      store.dispatch(
        getAutoCompleteSuggestionsAsync(this._id, this.addressInput, secondaryExpansion)
      );
    } else {
      this.hideAddressMenu();
      const addressInfo = {
        id: this._id,
        street1: suggestion.street1,
        street2: suggestion.street2,
        city: suggestion.city,
        state: suggestion.state,
        zipCode: suggestion.zipCode
      }
      emit({
        element: this,
        name: 'mhecomm-address-info',
        detail: addAnalyticsCheckoutFlowMetadata({id: this.id, addressInfo: addressInfo}),
      });
    }
  }

  /**
   * Add suggestions to us-autocomplete-pro-menu.
   * @param suggestions 
   * @returns 
   */
  private buildAddressMenu(suggestions: Suggestion[]) {
    this.showAddressMenu();
    if (suggestions.length > 0) {
      return suggestions.map((suggestion: Suggestion, index: number) => {
        const caret = html`
          <span class="float-end">${suggestion.entries > 1 ? html`${chevronRightIcon}` : nothing}</span>
        `;
        return html`<li id="active-option-${index}" role="option" aria-describedby="suggestion-${index}" 
        @click="${() => this._addressSelected(suggestion)}">
          <span style="display: none;" id="suggestion-${index}">${suggestion.displayAddress}</span>
          <div
            data-street-line="${suggestion.street1}"
            ?data-secondary="${suggestion.street2}"
            data-city="${suggestion.city}"
            data-state="${suggestion.state}"
          >
            ${this.highlightAddressInput(suggestion.displayAddress)}${caret}
          </div>
        </li>`;
      });
    } else {
      if(this.noSuggestionsFoundText){
        return html`<li>${this.noSuggestionsFoundText}</li>`;
      }
      else{
        return nothing;
      }
    }
  }

  /**
   * In us-autocomplete-pro-menu, make the suggestion text matching the address input text bold.
   * 
   * @param displayAddress 
   * @returns 
   */
  private highlightAddressInput(displayAddress: string) {
    const matchingAddressInputText = displayAddress.match(
      new RegExp(`^${this.addressInput}`, 'i')
    );
    if (matchingAddressInputText) {
      return html`<b
          >${displayAddress.slice(0, matchingAddressInputText[0].length)}</b
        >${displayAddress.slice(matchingAddressInputText[0].length)}`;
    }
    return html`${displayAddress}`;
  }

  /**
   * Filter PO box addresses from suggestions.
   * 
   * @param suggestions 
   * @returns 
   */
  private filterPoBoxAddresses(suggestions: Suggestion[]): Suggestion[] {
    return suggestions.filter((suggestion) => {
      return suggestion.displayAddress.toLowerCase().indexOf('po box') === -1;
    });
  }

  private inputElement(): HTMLInputElement {
    return this.shadowRoot!.querySelector(
      '#input-'+this.id
    )! as HTMLInputElement;
  }

  private addressMenuListItems(): HTMLCollectionOf<HTMLElement> {
    return this.shadowRoot!.querySelector(
      '#'+this.id+'-us-autocomplete-pro-menu'
    )?.getElementsByTagName('li')! as HTMLCollectionOf<HTMLElement>;
  }

  private showValidationStying(){
    return this.isDirty && !this.addressInput
  }

  protected submit(e){
    super.submit(e);
    emit({
      element: this,
      detail: {id: this.id, addressInfo: this.addressInput},
      name: this.FORM_SUBMIT_EVENT,
    })
  }

  focus(){
    this.inputElement().focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-address-selector': AddressSelector;
  }
}
